/**
 * User Hooks
 *
 * React Query hooks for user data
 */

import { usePermiso } from '@/application/auth'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import * as userService from './user.service'
import type {
  CreateUserData,
  UpdateUserData,
  ResetPasswordData,
  UserSearchFilters,
} from '@/domain/user/types'

/**
 * Query key for users
 */
export const userKeys = {
  all: ['users'] as const,
  lists: () => [...userKeys.all, 'list'] as const,
  list: (filters: UserSearchFilters) => [...userKeys.lists(), filters] as const,
  details: () => [...userKeys.all, 'detail'] as const,
  detail: (id: string) => [...userKeys.details(), id] as const,
  radiologists: () => [...userKeys.all, 'radiologists'] as const,
  profile: () => [...userKeys.all, 'profile'] as const,
}

/**
 * Hook to fetch users list
 */
export function useUsers(filters?: UserSearchFilters) {
  return useQuery({
    queryKey: userKeys.list(filters ?? {}),
    queryFn: () => userService.getUsers(filters),
  })
}

/**
 * Hook to fetch single user
 */
export function useUser(id: string) {
  return useQuery({
    queryKey: userKeys.detail(id),
    queryFn: () => userService.getUserById(id),
    enabled: !!id,
  })
}

/**
 * Hook to fetch radiologists with workload
 */
export function useRadiologists(options?: { enabled?: boolean }) {
  const { enabled = true } = options || {}
  // El endpoint exige worklist:manage: es la lista para repartir trabajo, no
  // un directorio. Pedirla sin el permiso devolvia 403 y dejaba el panel del
  // tecnologo con un bloque vacio y sin explicacion.
  const puede = usePermiso('worklist:manage')
  return useQuery({
    queryKey: userKeys.radiologists(),
    queryFn: userService.getRadiologists,
    staleTime: 30 * 1000, // 30 seconds - refresh frequently for workload
    enabled: enabled && puede,
  })
}

/**
 * Hook to create user
 */
export function useCreateUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateUserData) => userService.createUser(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() })
    },
  })
}

/**
 * Hook to update user
 */
export function useUpdateUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateUserData }) =>
      userService.updateUser(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() })
      queryClient.invalidateQueries({ queryKey: userKeys.detail(variables.id) })
    },
  })
}

/**
 * Hook to delete user
 */
export function useDeleteUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => userService.deleteUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() })
    },
  })
}

/**
 * Hook to reset user password
 */
export function useResetPassword() {
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ResetPasswordData }) =>
      userService.resetUserPassword(id, data),
  })
}

/**
 * Hook to activate user
 */
export function useActivateUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => userService.activateUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() })
    },
  })
}

/**
 * Hook to deactivate user
 */
export function useDeactivateUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => userService.deactivateUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() })
    },
  })
}

// =============================================================================
// Profile Hooks
// =============================================================================

/**
 * Hook to fetch current user profile
 */
export function useMyProfile() {
  return useQuery({
    queryKey: userKeys.profile(),
    queryFn: userService.getMyProfile,
  })
}

/**
 * Hook to update current user profile
 */
export function useUpdateMyProfile() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: userService.UpdateProfileData) => userService.updateMyProfile(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.profile() })
    },
  })
}

/**
 * Hook to change current user password
 */
export function useChangeMyPassword() {
  return useMutation({
    mutationFn: (data: userService.ChangePasswordData) => userService.changeMyPassword(data),
  })
}
